import React, { useCallback, useState, useEffect, useRef } from 'react'
import { NavLink } from 'react-router-dom'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { list, getUrl } from 'aws-amplify/storage'
import { flatMap, sortBy } from 'lodash'
import { CloseSimpleWhiteIcon, CartIcon, NotificationIcon, MenuLinesIcon, ColteneLogoMobile, ColteneModernLogo } from '../../../icons'
import useOnClickOutside from './useOnClickOutside'
import AccountMenu from '../../AccountMenu'
import StoreBasket from '../../ColteneStore/StoreBasket'
import Language from '../Language'
import NotificationsTimeline from '../../MyAccount/Notifications/NotificationsTimeline'
import { useAppContext } from '../../../libs/contextLib'
import { loadCurrentUserNotifications } from '../../../actions/notifications'
import { loadCorporateBranches } from '../../../actions/corporate'
import {
  bellNotificationsPermissions,
  checkPermission,
  hasCorporateAdminModulesPermission,
  hasEndUserSiteAccess,
  hasStorePermission,
} from '../../../utils/permissionValidation'
import { keyboardEvents } from '../../../utils/keyboardEvents'
import './banner.scss'

const Banner = (props) => {
  const { basket } = props
  const [src, setSrc] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const { isAuthenticated, supportsColteneStore } = useAppContext()
  const [hideAccountMenu, setHideAccountMenu] = useState(true)
  const [showStoreBasket, setShowStoreBasket] = useState(false)
  const [notifications, setNotifications] = useState([])
  const [isUnreadNotifications, setIsUnreadNotifications] = useState(false)
  const [showNotificationsTimeline, setShowNotificationsTimeline] = useState(false)
  const [notificationsLoading, setNotificationsLoading] = useState(false)
  const [pulse, setPulse] = useState(0)
  const [basketQuantity, setBasketQuantity] = useState(0)
  const [userPermissions, setUserPermissions] = useState([])
  const notificationsPollingIntervalMinutes = (process.env.REACT_APP_NOTIFICATION_FREQUENCY_SECONDS || 120) / 60
  const isCorporateAdmin = hasCorporateAdminModulesPermission(props.userPermissions)
  const companyId = props.companyId

  useEffect(() => {
    let isMounted = true
    setSrc('')
    setIsLoading(true)

    if (!isAuthenticated || !props.authedUser?.cognitoSub) {
      setIsLoading(false)
      return
    }
    ;(async () => {
      try {
        const files = await list({
          prefix: '',
          options: {
            accessLevel: 'protected',
            pageSize: 1000,
          },
        })
        if (!isMounted) return
        const avatar = (files?.items || []).find((file) => /my_avatar.*/i.test(file.key || ''))
        if (avatar?.key) {
          const result = await getUrl({
            key: avatar.key,
            options: {
              accessLevel: 'protected',
            },
          })
          if (!isMounted) return
          setSrc(result.url.toString())
        }
      } catch (e) {
        console.warn('Avatar load failed', e)
      } finally {
        if (isMounted) setIsLoading(false)
      }
    })()
    return () => {
      isMounted = false
    }
  }, [isAuthenticated, props.authedUser?.cognitoSub])

  useEffect(() => {
    const permissionArr = props.userPermissions ? (Array.isArray(props.userPermissions) ? props.userPermissions : Object.values(props.userPermissions)) : []
    setUserPermissions(permissionArr)
  }, [props.userPermissions])

  useEffect(() => {
    if (isCorporateAdmin && companyId) {
      props.actions.loadCorporateBranches(companyId)
    }
  }, [isCorporateAdmin, companyId])

  const transNotifications = (notifications) => {
    const res = flatMap(notifications)
    const data = sortBy(res, (e) => -new Date(e.notification_date))
    return data
  }

  useEffect(() => {
    const userProductSerialNumbers = Object.entries(props.products)
      .filter((item) => item[1].association_active)
      .map((item) => item[1].serial_number)
    let userNotifications = transNotifications(props.notificationsTimeline).filter(
      (item) => (item.type === 'device' && userProductSerialNumbers.includes(item.device_sn)) || item.type !== 'device',
    )
    if (isCorporateAdmin && props.corporateBranches?.length) {
      const branchIds = props.corporateBranches.map((branch) => branch.id)
      const corporateNotifications = transNotifications(props.notificationsTimeline).filter((item) => item.branch?.id && branchIds.includes(item.branch.id))
      const existingIds = new Set(userNotifications.map((item) => item.id))
      const mergedNotifications = [...userNotifications, ...corporateNotifications.filter((item) => !existingIds.has(item.id))]
      userNotifications = mergedNotifications
    }
    setNotifications(userNotifications)
  }, [props.products, props.notificationsTimeline, props.corporateBranches, isCorporateAdmin])

  useEffect(() => {
    let intervalId

    const loadNotifications = () => {
      if (props?.authedUser?.cognitoSub && checkPermission(props.userPermissions, bellNotificationsPermissions)) {
        setNotificationsLoading(true)
        props.actions.loadCurrentUserNotifications(props.authedUser.cognitoSub, isUnreadNotifications).finally(() => setNotificationsLoading(false))
      }
    }
    loadNotifications()

    intervalId = setInterval(loadNotifications, 60000 * notificationsPollingIntervalMinutes)

    return () => {
      if (intervalId) clearInterval(intervalId)
    }
  }, [props.authedUser?.cognitoSub, props.userPermissions, isUnreadNotifications])

  const onRefChange = useCallback(() => {
    setPulse(1)
  }, [basket])

  useEffect(() => {
    if (basket) {
      const total = Object.values(basket).reduce((acc, product) => acc + product.quantity, 0)
      setBasketQuantity(total)
    } else {
      setBasketQuantity(0)
    }
  }, [basket])

  const timelineRef = useRef(null)
  const handleAccountMenu = () => {
    setHideAccountMenu((prevState) => !prevState)
  }

  useOnClickOutside(timelineRef, () => setShowNotificationsTimeline(false))
  const handleIsUnreadNotificationsChange = () => setIsUnreadNotifications(!isUnreadNotifications)

  return (
    <>
      <header className={!isAuthenticated ? 'banner u-header' : 'banner'}>
        {!isAuthenticated ? (
          <div className="u-banner">
            <div className="banner-r">
              <div className="banner-language-wrapper">
                <Language />
              </div>
            </div>
          </div>
        ) : (
          <div className="a-banner">
            <div className="banner-l">
              <nav className="top-nav">
                {props.toggleNavDisplay && (
                  <div
                    tabIndex={0}
                    role="button"
                    className="toggle-nav-menu"
                    onClick={props.toggleNavDisplay}
                    onKeyDown={(event) => {
                      if (event.key === keyboardEvents.ENTER) props.toggleNavDisplay()
                    }}
                  >
                    {props.navDisplay ? <CloseSimpleWhiteIcon className="icon" /> : <MenuLinesIcon className="icon" />}
                  </div>
                )}
                <NavLink to="/" activeClassName="active">
                  <ColteneModernLogo alt="Scican Logo" className="logo" />
                  <ColteneLogoMobile alt="Scican Logo" className="logo-mobile" />
                </NavLink>
              </nav>
            </div>
            <div className="banner-r">
              <div className="banner-language-wrapper">
                <Language />
              </div>
              {checkPermission(props.userPermissions, bellNotificationsPermissions) && (
                <div
                  className={
                    'banner-notifications-wrapper notification-element' + (showNotificationsTimeline ? ' element-active notification-timeline-active' : '')
                  }
                  onClick={() => setShowNotificationsTimeline((prevState) => !prevState)}
                  tabIndex={0}
                  role="button"
                  onKeyDown={(event) => {
                    if (event.key === keyboardEvents.ENTER) setShowNotificationsTimeline((prevState) => !prevState)
                  }}
                >
                  <NotificationIcon />
                  {notifications?.filter((item) => !item.is_read)?.length > 0 && (
                    <div className="notifications-counter">
                      <div className="counter-badge">
                        {notifications?.filter((item) => !item.is_read)?.length > 9 ? '9+' : notifications?.filter((item) => !item.is_read)?.length}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {hasEndUserSiteAccess(userPermissions) && (
                <>
                  {supportsColteneStore && hasStorePermission(userPermissions) && !isCorporateAdmin && (
                    <div
                      className="banner-cart-wrapper"
                      onClick={() => setShowStoreBasket((prevState) => !prevState)}
                      tabIndex={0}
                      role="button"
                      onKeyDown={(event) => {
                        if (event.key === keyboardEvents.ENTER) setShowStoreBasket((prevState) => !prevState)
                      }}
                    >
                      <CartIcon />
                      {basketQuantity > 0 && (
                        <div ref={onRefChange} className="cart-product-number" onAnimationEnd={() => setPulse(0)} pulse={pulse}>
                          <div className="product-number">{basketQuantity > 9 ? '9+' : basketQuantity}</div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
              <div
                className={'banner-profile-wrapper profile-element' + (!hideAccountMenu ? ' element-active' : '')}
                onClick={handleAccountMenu}
                tabIndex={0}
                role="button"
                onKeyDown={(event) => {
                  if (event.key === keyboardEvents.ENTER) handleAccountMenu()
                }}
              >
                <div className={'avatar-title' + (!hideAccountMenu ? ' avatar-selected' : '')}>
                  {src ? (
                    <img className="banner-img" src={src} alt="avatar" />
                  ) : isLoading ? (
                    <div className="banner-img" />
                  ) : (
                    <div className="avatar-initials-small">
                      <span>{`${props.authedUser?.firstname?.charAt(0) || ''}${props.authedUser?.lastname?.charAt(0) || ''}`.trim()}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </header>
      {!hideAccountMenu && (
        <AccountMenu
          toggleShowOnlineAccess={props.toggleShowOnlineAccess}
          disableRemoteSession={props.disableRemoteSession}
          setHideAccountMenu={setHideAccountMenu}
          closeNavDisplay={props.closeNavDisplay}
          userAvatar={src}
        />
      )}
      <StoreBasket toggle={showStoreBasket} onToggle={() => setShowStoreBasket(false)} />
      <div ref={timelineRef}>
        {showNotificationsTimeline && (
          <NotificationsTimeline
            notifications={notifications}
            products={props.products}
            closeTimeline={() => setShowNotificationsTimeline(false)}
            isUnreadNotifications={isUnreadNotifications}
            handleIsUnreadNotificationsChange={handleIsUnreadNotificationsChange}
            notificationsLoading={notificationsLoading}
            isCorporateAdmin={isCorporateAdmin}
            corporateBranches={props.corporateBranches}
          />
        )}
      </div>
    </>
  )
}

function mapStateToProps({ authedUser, colteneStore, notifications, userPermissions, userProducts, corporate, userCompany }) {
  return {
    authedUser,
    basket: colteneStore?.basket,
    notificationsTimeline: notifications?.notificationsTimeline || [],
    userPermissions,
    products: userProducts?.products || {},
    corporateBranches: corporate?.branches || [],
    companyId: userCompany?.companyId,
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadCurrentUserNotifications,
        loadCorporateBranches,
      },
      dispatch,
    ),
  }
}

export default connect(mapStateToProps, dispatchToProps)(Banner)

